/**
 * Timezone conversion utilities.
 * Converts session times stored in a batch's timezone to the learner's local timezone.
 */

/**
 * Get the user's local timezone from the browser.
 */
export function getLocalTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
}

const COMMON_TIMEZONE_LABELS = new Map([
  ["UTC", "UTC"],
  ["Asia/Kolkata", "India — Kolkata"],
  ["America/New_York", "USA/Canada — Eastern"],
  ["America/Chicago", "USA/Canada — Central"],
  ["America/Denver", "USA/Canada — Mountain"],
  ["America/Los_Angeles", "USA/Canada — Pacific"],
  ["America/Toronto", "Canada — Toronto"],
  ["Europe/London", "United Kingdom — London"],
  ["Europe/Berlin", "Germany — Berlin"],
  ["Europe/Paris", "France — Paris"],
  ["Asia/Dubai", "UAE — Dubai"],
  ["Asia/Singapore", "Singapore"],
  ["Asia/Tokyo", "Japan — Tokyo"],
  ["Australia/Sydney", "Australia — Sydney"],
  ["Pacific/Auckland", "New Zealand — Auckland"],
]);

const TIMEZONE_REGION_LABELS = {
  Africa: "Africa",
  America: "Americas",
  Antarctica: "Antarctica",
  Arctic: "Arctic",
  Asia: "Asia",
  Atlantic: "Atlantic Ocean",
  Australia: "Australia",
  Europe: "Europe",
  Indian: "Indian Ocean",
  Pacific: "Pacific Ocean",
};

export function normalizeTimezoneForForm(timezone) {
  return timezone === "Asia/Calcutta" ? "Asia/Kolkata" : timezone;
}

function getTimezoneOffsetLabel(timezone, at = new Date()) {
  try {
    const instant = isDateOnlyValue(at)
      ? new Date(zonedDateTimeToIso(`${at}T12:00:00`, timezone))
      : at;
    const offset = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "longOffset",
    })
      .formatToParts(instant)
      .find((part) => part.type === "timeZoneName")?.value;
    if (!offset || offset === "GMT") return "UTC";
    return offset
      .replace(/^GMT/, "UTC")
      .replace(/([+-])0(\d)/, "$1$2")
      .replace(/:00$/, "");
  } catch {
    return "";
  }
}

export function formatTimezoneOptionLabel(timezone, at = new Date()) {
  const normalized = normalizeTimezoneForForm(timezone);
  const commonLabel = COMMON_TIMEZONE_LABELS.get(normalized);
  const [region, ...placeParts] = String(normalized || "").split("/");
  const place = placeParts.join(" / ").replaceAll("_", " ");
  const locationLabel = commonLabel ||
    (place
      ? `${TIMEZONE_REGION_LABELS[region] || region} — ${place}`
      : normalized);
  const offset = getTimezoneOffsetLabel(normalized, at);
  return offset && locationLabel !== offset
    ? `${locationLabel} (${offset})`
    : locationLabel;
}

export function getTimezoneOptions({
  supportedTimezones,
  currentTimezone,
  at = new Date(),
} = {}) {
  let available = supportedTimezones;
  if (!available) {
    try {
      available = Intl.supportedValuesOf("timeZone");
    } catch {
      available = [];
    }
  }

  const normalizedCurrent = normalizeTimezoneForForm(currentTimezone);
  const ordered = [
    ...COMMON_TIMEZONE_LABELS.keys(),
    ...(normalizedCurrent ? [normalizedCurrent] : []),
    ...available.map(normalizeTimezoneForForm),
  ];

  return [...new Set(ordered.filter(Boolean))].map((timezone) => ({
    value: timezone,
    label: formatTimezoneOptionLabel(timezone, at),
  }));
}

export function getVenueTimezoneSuggestion({ city, country } = {}) {
  const location = `${city || ""} ${country || ""}`.trim().toLowerCase();
  if (
    /\b(india|bharat|in)\b/.test(location) ||
    /\b(bangalore|bengaluru)\b/.test(location)
  ) {
    return "Asia/Kolkata";
  }
  return null;
}

export function formatDateOnly(date, options = {}) {
  const match = String(date || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  const [, year, month, day] = match;
  const hasDateFields = ["weekday", "year", "month", "day"].some((key) =>
    Object.hasOwn(options, key),
  );
  return new Intl.DateTimeFormat("en-US", {
    ...(hasDateFields
      ? {}
      : { month: "short", day: "numeric", year: "numeric" }),
    ...options,
    timeZone: "UTC",
  }).format(Date.UTC(Number(year), Number(month) - 1, Number(day)));
}

export function isDateOnlyValue(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));
}

export function formatDateValue(value, options = {}) {
  if (!value) return "";
  if (isDateOnlyValue(value)) return formatDateOnly(value, options);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const hasDateFields = ["weekday", "year", "month", "day"].some((key) =>
    Object.hasOwn(options, key),
  );
  return new Intl.DateTimeFormat("en-US", {
    ...(hasDateFields
      ? {}
      : { month: "short", day: "numeric", year: "numeric" }),
    ...options,
  }).format(date);
}

export function getDateKeyInTimezone(
  value = new Date(),
  timezone = getLocalTimezone(),
) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const parts = getDateParts(formatter, date);
    return `${parts.year}-${parts.month}-${parts.day}`;
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

export function getDefaultSessionTime({
  sessionDate,
  timezone = getLocalTimezone(),
  now = new Date(),
} = {}) {
  const normalHours = { start: "09:00", end: "17:00" };
  if (sessionDate !== getDateKeyInTimezone(now, timezone)) return normalHours;

  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = getDateParts(formatter, now);
  const currentMinutes = Number(parts.hour) * 60 + Number(parts.minute);
  if (currentMinutes < 9 * 60) return normalHours;

  const roundedStart = Math.ceil(
    (currentMinutes + (Number(parts.second) > 0 ? 1 : 0)) / 30,
  ) * 30;
  if (roundedStart >= 24 * 60) return { start: "", end: "" };

  const formatMinutes = (minutes) =>
    `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
  const endMinutes = Math.min(
    23 * 60 + 59,
    Math.max(17 * 60, roundedStart + 60),
  );

  return {
    start: formatMinutes(roundedStart),
    end: formatMinutes(endMinutes),
  };
}

export function differenceInCalendarDays(
  targetDate,
  reference = new Date(),
  timezone = getLocalTimezone(),
) {
  if (!isDateOnlyValue(targetDate)) return null;
  const referenceDate = isDateOnlyValue(reference)
    ? String(reference)
    : getDateKeyInTimezone(reference, timezone);
  if (!referenceDate) return null;
  const toUtcDay = (date) => {
    const [year, month, day] = date.split("-").map(Number);
    return Date.UTC(year, month - 1, day);
  };
  return Math.round(
    (toUtcDay(targetDate) - toUtcDay(referenceDate)) / (24 * 60 * 60 * 1000),
  );
}

export function zonedDateTimeToIso(localDateTime, timezone) {
  if (!localDateTime) return null;
  const normalized =
    localDateTime.length === 16 ? `${localDateTime}:00` : localDateTime;
  const rough = new Date(`${normalized}Z`);
  if (Number.isNaN(rough.getTime())) return null;
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  // Match PostgreSQL's `timestamp AT TIME ZONE zone` policy. Repeated times
  // use the later instant; nonexistent spring-forward times move forward by
  // the DST gap. Sampling nearby offsets avoids fixed-point oscillation.
  const desiredWallClock = rough.getTime();
  const offsets = new Set(
    [-168, -48, -24, 0, 24, 48, 168].map((hours) =>
      getTimezoneOffsetMs(
        formatter,
        new Date(desiredWallClock + hours * 60 * 60 * 1000),
      ),
    ),
  );
  const candidates = [...offsets].map((offset) => {
    const instant = new Date(desiredWallClock - offset);
    const displayedWallClock = partsAsUtcMs(getDateParts(formatter, instant));
    return {
      instant,
      wallClockDifference: displayedWallClock - desiredWallClock,
    };
  });
  const exact = candidates
    .filter((candidate) => candidate.wallClockDifference === 0)
    .sort((a, b) => b.instant.getTime() - a.instant.getTime());
  if (exact.length > 0) return exact[0].instant.toISOString();

  const shiftedForward = candidates
    .filter((candidate) => candidate.wallClockDifference > 0)
    .sort(
      (a, b) =>
        a.wallClockDifference - b.wallClockDifference ||
        b.instant.getTime() - a.instant.getTime(),
    );
  const fallback = shiftedForward[0] ||
    candidates.sort(
      (a, b) =>
        Math.abs(a.wallClockDifference) - Math.abs(b.wallClockDifference),
    )[0];
  return fallback?.instant.toISOString() || null;
}

function partsAsUtcMs(parts) {
  return Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
}

function getTimezoneOffsetMs(formatter, instant) {
  return partsAsUtcMs(getDateParts(formatter, instant)) - instant.getTime();
}

export function formatDateTimeInput(value, timezone) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = getDateParts(formatter, date);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

/**
 * Convert a date + time from a source timezone to the user's local timezone.
 * @param {string} date - Date string (YYYY-MM-DD)
 * @param {string} time - Time string (HH:MM or HH:MM:SS)
 * @param {string} sourceTimezone - IANA timezone of the source (e.g. 'Asia/Kolkata')
 * @returns {{ localDate: string, localTime: string, localTimezone: string }}
 */
function convertToTimezone(date, time, sourceTimezone, targetTimezone) {
  if (!date || !time)
    return {
      localDate: date,
      localTime: time,
      localTimezone: targetTimezone,
      instant: null,
    };

  try {
    const timeNormalized = time.length === 5 ? `${time}:00` : time;
    const iso = zonedDateTimeToIso(
      `${date}T${timeNormalized}`,
      sourceTimezone,
    );
    if (!iso) throw new Error("Invalid session date or time");
    const instant = new Date(iso);
    const localFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: targetTimezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const localParts = getDateParts(localFormatter, instant);

    return {
      localDate: `${localParts.year}-${localParts.month}-${localParts.day}`,
      localTime: `${localParts.hour}:${localParts.minute}`,
      localTimezone: targetTimezone,
      instant,
    };
  } catch {
    return {
      localDate: date,
      localTime: time.slice(0, 5),
      localTimezone: targetTimezone,
      instant: null,
    };
  }
}

export function convertToLocalTime(date, time, sourceTimezone) {
  return convertToTimezone(date, time, sourceTimezone, getLocalTimezone());
}

/**
 * Format a time string to 12-hour format.
 */
export function formatTime12h(time) {
  if (!time) return "";
  const [h, m] = time.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}

/**
 * Format a session's time range with timezone conversion.
 * Shows "(your time)" indicator when timezone differs.
 */
export function formatSessionTimeRange(
  startTime,
  endTime,
  sessionDate,
  batchTimezone,
  viewerTimezone = getLocalTimezone(),
) {
  const sourceStart = String(startTime || "").slice(0, 5);
  const sourceEnd = String(endTime || "").slice(0, 5);
  const startLocal = convertToTimezone(
    sessionDate,
    startTime,
    batchTimezone,
    viewerTimezone,
  );
  const endLocal = endTime
    ? convertToTimezone(
        sessionDate,
        endTime,
        batchTimezone,
        viewerTimezone,
      )
    : null;

  const start = formatTime12h(startLocal.localTime);
  const end = endLocal ? ` - ${formatTime12h(endLocal.localTime)}` : "";
  const converted =
    startLocal.localDate !== sessionDate ||
    startLocal.localTime !== sourceStart ||
    Boolean(
      endLocal &&
        (endLocal.localDate !== sessionDate || endLocal.localTime !== sourceEnd),
    );
  const sourceInstant = startLocal.instant;
  const localDateLabel = formatDateOnly(startLocal.localDate, {
    month: "short",
    day: "numeric",
  });
  const localEndDateLabel = endLocal
    ? formatDateOnly(endLocal.localDate, { month: "short", day: "numeric" })
    : localDateLabel;
  const localDisplayWithDate = endLocal && endLocal.localDate !== startLocal.localDate
    ? `${localDateLabel} ${start} - ${localEndDateLabel} ${formatTime12h(endLocal.localTime)}`
    : startLocal.localDate !== sessionDate
      ? `${localDateLabel} ${start}${end}`
      : `${start}${end}`;

  return {
    display: `${start}${end}${endLocal && endLocal.localDate !== startLocal.localDate ? " (next day)" : ""}`,
    converted,
    originalDisplay: `${formatTime12h(startTime)}${endTime ? ` - ${formatTime12h(endTime)}` : ""}`,
    localTimezone: shortTimezone(viewerTimezone, sourceInstant),
    originalTimezone: shortTimezone(batchTimezone, sourceInstant),
    dateChanged: startLocal.localDate !== sessionDate,
    endDateChanged: Boolean(endLocal && endLocal.localDate !== sessionDate),
    localDate: startLocal.localDate,
    localEndDate: endLocal?.localDate || startLocal.localDate,
    localDisplayWithDate,
    originalDate: sessionDate,
  };
}

/** Extract parts from a DateTimeFormat */
function getDateParts(formatter, date) {
  const parts = formatter.formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value || "00";
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour") === "24" ? "00" : get("hour"),
    minute: get("minute"),
    second: get("second"),
  };
}

/** Get short timezone abbreviation */
function shortTimezone(tz, instant) {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    });
    const parts = formatter.formatToParts(instant || new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value || tz;
  } catch {
    return tz;
  }
}

export function isBatchEnrollmentClosed(deadline, timezone, now = new Date()) {
  return Boolean(deadline && getDateKeyInTimezone(now, timezone || "UTC") > deadline);
}
